/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import type { TablePaginationConfig, TableProps } from "antd"
import { Button, Popconfirm, Table, Tag, Tooltip, Typography } from "antd"
import { EditOutlined, DeleteOutlined, LockOutlined, UnlockOutlined } from "@ant-design/icons"
import type React from "react"
import { formatDateTime } from "@/utils/dateUtils"
import type { SettingItem } from "@/services/system/settingApi"

const { Text } = Typography

interface SettingsTableProps {
  data: SettingItem[]
  loading: boolean
  pagination?: TableProps["pagination"]
  onEdit: (record: SettingItem) => void
  onDelete: (record: SettingItem) => void
}

const SETTING_FRIENDLY_NAMES: Record<string, { label: string; color: string; desc: string }> = {
  others_learning_ids: {
    label: "Người khác cũng đang học",
    color: "blue",
    desc: "Ghim đầu trang danh sách /sets trên Web",
  },
  featured_ids: {
    label: "Bộ từ nổi bật",
    color: "gold",
    desc: "Hiển thị tại Trang chủ Dashboard và Trang Tìm kiếm",
  },
}

const GROUP_COLORS: Record<string, string> = {
  sets: "geekblue",
  system: "purple",
  home: "cyan",
  banner: "orange",
  gamification: "green",
}

export const SettingsTable: React.FC<SettingsTableProps> = ({
  data,
  loading,
  pagination,
  onEdit,
  onDelete,
}) => {
  const columns: TableProps<SettingItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
      align: "center",
      render: (val: number) => <span className="font-semibold">{val}</span>,
    },
    {
      title: "Nhóm (Group)",
      dataIndex: "group",
      key: "group",
      width: 130,
      render: (group: string) => {
        const color = GROUP_COLORS[group] || "default"
        return (
          <Tag color={color} className="font-medium text-xs">
            {group}
          </Tag>
        )
      },
    },
    {
      title: "Tên cấu hình (Name)",
      key: "name",
      width: 280,
      render: (_: any, record: SettingItem) => {
        const friendly = SETTING_FRIENDLY_NAMES[record.name]
        return (
          <div className="flex flex-col gap-0.5">
            <span className="font-mono font-semibold text-primary text-sm">
              {record.name}
            </span>
            {friendly && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <Tag color={friendly.color} className="m-0 text-[11px] py-0 px-1 font-medium">
                  {friendly.label}
                </Tag>
                <Text type="secondary" className="text-[11px] italic">
                  {friendly.desc}
                </Text>
              </div>
            )}
          </div>
        )
      },
    },
    {
      title: "Khóa (Locked)",
      dataIndex: "locked",
      key: "locked",
      width: 120,
      align: "center",
      render: (locked: number) => {
        if (locked === 1) {
          return (
            <Tag icon={<LockOutlined />} color="error" className="text-xs">
              Khóa hệ thống
            </Tag>
          )
        }
        return (
          <Tag icon={<UnlockOutlined />} color="success" className="text-xs">
            Cho phép sửa
          </Tag>
        )
      },
    },
    {
      title: "Dữ liệu (Payload preview)",
      key: "payload",
      render: (_: any, record: SettingItem) => {
        const payload = record.payload
        if (Array.isArray(payload)) {
          return (
            <div className="max-w-[350px] truncate text-xs font-mono bg-gray-50 px-2 py-1 rounded border border-gray-100">
              <span className="text-blue-600 font-semibold">{payload.length} phần tử: </span>
              {JSON.stringify(payload)}
            </div>
          )
        }
        return (
          <div className="max-w-[350px] truncate text-xs font-mono bg-gray-50 px-2 py-1 rounded border border-gray-100">
            {typeof payload === "object" ? JSON.stringify(payload) : String(payload)}
          </div>
        )
      },
    },
    {
      title: "Cập nhật lần cuối",
      dataIndex: "updated_at",
      key: "updated_at",
      width: 140,
      align: "center",
      render: (val: string) => (val ? formatDateTime(val, "dd/mm/yy hh:mm") : "—"),
    },
    {
      title: "Thao tác",
      key: "actions",
      width: 100,
      align: "center",
      fixed: "right",
      render: (_: any, record: SettingItem) => (
        <div className="flex items-center justify-center gap-1">
          <Tooltip title="Chỉnh sửa cấu hình">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined className="text-blue-600" />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>

          {record.locked === 1 ? (
            <Tooltip title="Cấu hình hệ thống bị khóa, không thể xóa">
              <Button type="text" size="small" icon={<DeleteOutlined className="text-gray-300" />} disabled />
            </Tooltip>
          ) : (
            <Popconfirm
              title="Xác nhận xóa cấu hình?"
              description={`Bạn có chắc muốn xóa cấu hình "${record.name}" khỏi nhóm "${record.group}"?`}
              onConfirm={() => onDelete(record)}
              okText="Xóa"
              cancelText="Hủy"
              okButtonProps={{ danger: true }}
            >
              <Tooltip title="Xóa cấu hình">
                <Button type="text" size="small" danger icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </div>
      ),
    },
  ]

  return (
    <Table
      bordered
      columns={columns}
      dataSource={data}
      rowKey="id"
      loading={loading}
      pagination={pagination as TablePaginationConfig}
      size="middle"
      scroll={{ x: 1000 }}
    />
  )
}
